import React, { useEffect, useMemo, useState } from 'react'
import { t } from 'i18next'
import { object } from 'yup'
import Button from '@node-space/storybook-components/dist/Button'
import { DocumentUpload } from '@node-space/storybook-components/dist/UploadFile'
import {
  AccountContactTypes,
  PersonDefaultOptions,
} from 'components/accountContact/helpers/constants'
import { AccountContactData, PersonSelectorData } from 'components/accountContact/helpers/types'
import { PersonSelector } from 'components/accountContact/PersonSelector'
import { useFormFields } from 'hooks/forms/useFormFields'
import { useFormSetup } from 'hooks/forms/useFormSetup'
import { useYupFields } from 'hooks/forms/useYupFields'
import { FormBody } from 'pages/Verification/_components/FormBody'
import { FormSubmitButton } from 'pages/Verification/_components/FormSubmitButton'
import { FormSubmitError } from 'pages/Verification/_components/FormSubmitError'
import { FormSchemaType } from 'types/formField'
import { getSignatoryLabel } from 'utils/utils'
import { DocumentInput } from '../../UploadDocuments/UploadDocumentsV2/components/DocumentInput'
import { useUploadDocument } from '../hooks/useDocumentUpload'
import { useSignatory } from '../hooks/useSignatory'
import { SignatoryEmailSection } from './SignatoryEmailSection'

type AppointSignatoryFormProps = {
  personSelectorData: PersonSelectorData
  accountContactsList: AccountContactData[]
  onContinue: () => void
}

export const AppointSignatoryForm = ({
  personSelectorData,
  accountContactsList,
  onContinue,
}: AppointSignatoryFormProps) => {
  const [formDocument, setFormDocument] = useState<DocumentUpload>(null)
  const {
    profile,
    preferencesData,
    selectedSignatory,
    selectedReference,
    emailSent,
    handlePersonChange,
    apiError,
    setApiError,
    isLoadingSubmitRequest,
    onSubmit,
    removeSignatory,
    isUpdatingSignatoryPreference,
    isDeletingTeamMember,
    isLoadingAccountUsers,
  } = useSignatory(personSelectorData, accountContactsList, onContinue)

  const { uploadDocument, isDocumentLoading, isUpdatingPreference } = useUploadDocument({
    onSuccess: onContinue,
    onError: error => setApiError(error?.message || 'Failed to upload document'),
    selectedSignatory: selectedSignatory,
  })

  type SignatoryFormData = { emailAddress: string }
  type SignatoryFormKey = keyof SignatoryFormData

  const { getEmailYupField } = useYupFields()
  const defaultValues = { emailAddress: '' }
  const formSchema = object<FormSchemaType<SignatoryFormKey>>({
    emailAddress: getEmailYupField(t('formFields.emailAddress')),
  })
  const { form, formValues } = useFormSetup<SignatoryFormData>(formSchema, defaultValues)
  const { setEmailField } = useFormFields<SignatoryFormKey>(formSchema, form, formValues)
  const emailField = setEmailField({ key: 'emailAddress' })

  useEffect(() => {
    form.setValue('emailAddress', selectedSignatory?.emailAddress || '')
  }, [selectedSignatory, form])

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = form

  const isEmailMatching = profile?.emailAddress === selectedSignatory?.emailAddress
  const isMyselfSelected =
    selectedReference === PersonDefaultOptions.MYSELF || selectedSignatory?.isMyself
  const contact = selectedSignatory ? getSignatoryLabel(selectedSignatory) : ''
  const isLoading =
    isSubmitting || isLoadingSubmitRequest || isUpdatingPreference || isDocumentLoading

  const isAccountRepOwnerOrDirector = useMemo(() => {
    if (!selectedSignatory) return false

    if (isMyselfSelected && selectedSignatory?.reference === selectedSignatory?.emailAddress) {
      return (
        accountContactsList?.some(
          ({ person, type }) =>
            person?.reference === selectedSignatory?.reference &&
            type !== AccountContactTypes.ACCOUNT_REPRESENTATIVE
        ) &&
        accountContactsList?.some(
          ({ person, type }) =>
            person?.reference === selectedSignatory?.reference &&
            type === AccountContactTypes.ACCOUNT_REPRESENTATIVE
        )
      )
    } else if (isMyselfSelected) {
      const accountRep = accountContactsList?.find(
        person =>
          person?.contactInfo?.emailAddress === selectedSignatory?.emailAddress &&
          person?.type === AccountContactTypes.ACCOUNT_REPRESENTATIVE
      )
      if (!accountRep) return false

      // Check if the account representative is also an owner or director.
      return accountContactsList?.some(
        person =>
          person.type !== AccountContactTypes.ACCOUNT_REPRESENTATIVE &&
          person?.person?.reference === accountRep?.person?.reference
      )
    }
  }, [selectedSignatory, accountContactsList])

  const isDisabled = useMemo(() => {
    if (!selectedSignatory) return true
    if (isMyselfSelected && isAccountRepOwnerOrDirector) return false
    if (isMyselfSelected && !formDocument && !preferencesData?.selectedSignatory?.reference)
      return true
    if (!isMyselfSelected && !preferencesData?.selectedSignatory?.reference && isEmailMatching)
      return true
    if (preferencesData?.selectedSignatory?.reference && !isEmailMatching) return true
    return false
  }, [
    selectedSignatory,
    isMyselfSelected,
    formDocument,
    preferencesData,
    isEmailMatching,
    isAccountRepOwnerOrDirector,
  ])

  const handleContinue = () => {
    if (isMyselfSelected && formDocument && !isAccountRepOwnerOrDirector) {
      uploadDocument(formDocument)
    } else {
      handleSubmit(onSubmit)()
    }
  }

  return (
    <form>
      <FormBody>
        {apiError && <FormSubmitError>{apiError}</FormSubmitError>}
        <PersonSelector
          label={t('formFields.signatory')}
          otherPersons={personSelectorData?.personsData}
          selectedReference={selectedReference}
          onPersonChange={handlePersonChange}
          showSomeoneElseOption={false}
          showPosition={true}
          disabled={!!preferencesData?.selectedSignatory?.reference}
        />
        {isMyselfSelected &&
        !preferencesData?.selectedSignatory?.reference &&
        !isAccountRepOwnerOrDirector ? (
          <>
            <DocumentInput
              id={{
                category: t('verification.appointSignatory.powerOfAttorney'),
                code: t('verification.appointSignatory.powerOfAttorney'),
                contactReference: 'some-contact-reference',
              }}
              title={t('verification.appointSignatory.powerOfAttorney')}
              warning={t('verification.appointSignatory.documentsInformation.warning')}
              file={formDocument}
              provided={!!formDocument}
              onChooseFile={setFormDocument}
              onRemoveFile={() => setFormDocument(null)}
            />
          </>
        ) : (
          selectedSignatory?.reference &&
          !isAccountRepOwnerOrDirector && (
            <SignatoryEmailSection
              emailAddress={selectedSignatory?.emailAddress}
              isEmailMatching={isEmailMatching}
              emailSent={emailSent}
              contact={contact}
              preferencesReference={preferencesData?.selectedSignatory?.reference}
              emailField={emailField}
            />
          )
        )}

        {preferencesData?.selectedSignatory?.reference && (
          <Button
            type="button"
            secondary
            loading={isLoadingAccountUsers || isUpdatingSignatoryPreference || isDeletingTeamMember}
            onClick={removeSignatory}
          >
            {t('formFields.reassignSignatory')}
          </Button>
        )}

        <FormSubmitButton
          buttonText={t('continue')}
          loading={isLoading}
          disabled={isDisabled}
          testId="continue-button"
          onClick={handleContinue}
        />
      </FormBody>
    </form>
  )
}
